import { useExternalStoreRuntime, type ThreadMessageLike } from '@assistant-ui/react';
import type { ThreadAssistantMessagePart } from '@assistant-ui/react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { apiClient } from './apiClient';
import { getIdentity } from './config/services';
import type { ExecutionEvent } from '@/components/assistant-ui/execution-log';

/** Skill catalog entry for file-based skill loading */
export interface SkillCatalogEntry {
  base_path: string;
  agent_id: string;
}

/**
 * User input type for ask_user feature
 */
export type UserInputType = 'free_text' | 'single_select' | 'multi_select' | 'confirmation' | 'resource_form';

/**
 * Waiting for user input state (ask_user feature)
 */
export interface WaitingForUserInput {
  executionId: string;
  toolUseId: string;
  question: string;
  options?: string[];
  inputType: UserInputType;
  timeoutAt?: string;
  // Resource form specific fields (for inputType="resource_form")
  formSchema?: Record<string, unknown>;
  formValues?: Record<string, unknown>;
  formAction?: 'create' | 'edit' | 'view';
  resourceType?: string;
  resourceId?: string;
}

/**
 * Inline agent YAML for dynamic agent execution
 */
export interface InlineAgentYaml {
  name: string;
  description?: string;
  sdk?: 'eureka_agent' | 'openai_agents' | 'langgraph';
  execution_mode?: 'mcp_stdio'; // Required for inline agents
  llm?: {
    required?: boolean;
    model?: string;
    provider?: string;
  };
  /** Tools: string shorthand ("ask_user") or dict with agent field for sub-agents */
  tools?: Array<string | { name: string; agent?: string; description?: string; [key: string]: unknown }>;
  mcp_servers?: Array<{
    name: string;
    type_template?: string;
    required?: boolean;
  }>;
}

/**
 * Resource bindings for inline agent execution
 */
export interface InlineResourceBindings {
  resource_bindings?: Record<string, string>;
  tool_config?: Record<string, string>;
}

/**
 * Configuration options for useEurekaRuntime
 */
export interface EurekaRuntimeOptions {
  // Conversation tracking (optional - enables lifecycle features)
  conversationId?: string | null;
  ensureConversation?: (goal?: string) => Promise<string>;  // Called before first agent start
  onExecutionStarted?: (executionId: string, agentType: string) => void;

  // Identity context (optional - for multi-tenancy)
  tenantId?: string;
  projectId?: string;
  userId?: string;

  // Agent configuration
  defaultAgentId?: string;

  // Base path for file-based agents (catalog root directory)
  basePath?: string;

  // Skill catalog for Developer Agent
  skillCatalog?: Record<string, SkillCatalogEntry>;

  // Working directory for agent execution
  workingDirectory?: string;

  // Environment ID for per-environment bindings in runtime_binding.yaml
  environmentId?: string;

  // Developer persona for skill filtering (platform or agent)
  persona?: 'platform' | 'agent';

  // Generic configuration passthrough (any additional config from extension)
  // This is merged with base_path, skill_catalog, working_directory, environment_id, persona
  configuration?: Record<string, unknown>;

  // Initial messages (for restoring conversation history)
  initialMessages?: ThreadMessageLike[];

  // Initial execution events (for restoring historical execution log)
  initialEvents?: ExecutionEvent[];

  // === Inline Agent Execution (for Agent Builder) ===
  // When provided, executor uses these instead of loading from disk/WFM
  // Only needed on first turn - executor restores from DB on subsequent turns

  /** Inline agent YAML configuration */
  agentYaml?: InlineAgentYaml;

  /** Inline system prompt (instructions) */
  promptMd?: string;

  /** Resource bindings for inline agents (LLM, MCP servers) */
  resourceBindings?: InlineResourceBindings;
}

/**
 * Generate a simple unique ID
 */
function generateId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * JSON-compatible types for tool arguments
 */
type JSONValue = string | number | boolean | null | JSONObject | JSONArray;
type JSONObject = { readonly [key: string]: JSONValue };
type JSONArray = readonly JSONValue[];

/**
 * Tool call part for assistant messages - matches assistant-ui's ToolCallMessagePart
 */
type ToolCallPart = {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: JSONObject;
  argsText: string;
  result?: unknown;
  isError?: boolean;
};

/**
 * Text part for messages
 */
type TextPart = {
  type: 'text';
  text: string;
};

type MessagePart = TextPart | ToolCallPart;

/**
 * Eureka runtime using ExternalStoreRuntime for proper tool-call rendering
 *
 * Key improvements over LocalRuntime:
 * - Tool calls render as structured UI components (not plain text)
 * - Full control over message state
 * - Proper tool result display
 * - Conversation tracking for agent lifecycle (when conversationId provided)
 */
export function useEurekaRuntime(options: EurekaRuntimeOptions = {}) {
  const {
    conversationId,
    ensureConversation,
    onExecutionStarted,
    tenantId,
    projectId,
    userId,
    defaultAgentId = 'explorer/eureka_explorer',
    basePath,
    skillCatalog,
    workingDirectory,
    environmentId,
    persona,
    configuration: extraConfiguration,
    initialMessages = [],
    initialEvents = [],
    // Inline agent execution fields
    agentYaml,
    promptMd,
    resourceBindings,
  } = options;

  // DEBUG: Log every time useEurekaRuntime is called
  console.log('🔄 [useEurekaRuntime] Hook called with environmentId:', environmentId);

  // Use refs to ensure we always have the latest values in callbacks
  // This avoids stale closure issues with useCallback
  const workingDirectoryRef = useRef(workingDirectory);
  workingDirectoryRef.current = workingDirectory;
  // Message state - use initialMessages directly, update when it changes
  const [messages, setMessages] = useState<ThreadMessageLike[]>(initialMessages);
  const [isRunning, setIsRunning] = useState(false);

  // WebSocket connection status
  const [isConnected, setIsConnected] = useState(false);

  // Waiting for user input state (ask_user feature)
  const [waitingForUserInput, setWaitingForUserInput] = useState<WaitingForUserInput | null>(null);

  // Sync messages when initialMessages changes (component re-mounted with new conversation)
  const prevInitialMessagesRef = useRef<ThreadMessageLike[]>(initialMessages);
  useEffect(() => {
    // Only update if initialMessages actually changed (reference comparison)
    if (prevInitialMessagesRef.current !== initialMessages) {
      console.log('🔄 Syncing messages from initialMessages:', initialMessages.length);
      setMessages(initialMessages);
      prevInitialMessagesRef.current = initialMessages;
    }
  }, [initialMessages]);

  // Execution events for log display - initialize with historical events
  const [executionEvents, setExecutionEvents] = useState<ExecutionEvent[]>(initialEvents);
  const eventsRef = useRef<ExecutionEvent[]>(initialEvents);

  // Sync events when initialEvents changes (component re-mounted with new conversation)
  const prevInitialEventsRef = useRef<ExecutionEvent[]>(initialEvents);
  useEffect(() => {
    if (prevInitialEventsRef.current !== initialEvents) {
      console.log('🔄 Syncing events from initialEvents:', initialEvents.length);
      eventsRef.current = initialEvents;
      setExecutionEvents(initialEvents);
      prevInitialEventsRef.current = initialEvents;
    }
  }, [initialEvents]);

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const currentAssistantIdRef = useRef<string | null>(null);
  const partsRef = useRef<MessagePart[]>([]);
  // Track the index of the current text part we're appending to
  // Reset to -1 after adding a tool call so next text creates a new part
  const currentTextPartIdxRef = useRef<number>(-1);
  // Track the root execution ID (conversation ID) for resume requests
  const rootExecutionIdRef = useRef<string | null>(null);
  // Track the current agent execution ID to know when to close WebSocket
  const currentAgentExecutionIdRef = useRef<string | null>(null);
  // Track intentional WebSocket closes (for resume) to skip cleanup in onclose
  const isIntentionalCloseRef = useRef<boolean>(false);
  // Track uploaded resources: attachment ID -> resource ID
  // This is needed because @assistant-ui/react may strip custom properties
  const uploadedResourcesRef = useRef<Map<string, string>>(new Map());

  /**
   * Add an execution event to the log
   * Uses a distributive type to properly handle the union of event types
   * Automatically associates event with current assistant message
   *
   * For llm_end events: updates existing event with same execution_id instead of adding duplicate
   * This handles the case where backend sends multiple llm_end events or events are processed twice
   */
  type OmitAutoFields<T> = T extends { timestamp: number; messageId: string } ? Omit<T, 'timestamp' | 'messageId'> : never;
  const addEvent = useCallback((event: OmitAutoFields<ExecutionEvent>) => {
    const messageId = currentAssistantIdRef.current || 'unknown';
    const eventWithFields = { ...event, timestamp: Date.now(), messageId } as ExecutionEvent;

    // For llm_end events, deduplicate only when the content is identical
    // (prevents true duplicates from backend retransmissions).
    // Do NOT deduplicate by execution_id alone — an agent makes multiple LLM
    // calls within one execution (think → tool → think), each producing a
    // separate llm_end event that must be preserved.
    if (event.type === 'llm_end') {
      const newContent = JSON.stringify((event as any).content);
      const existingIdx = eventsRef.current.findIndex(
        e => e.type === 'llm_end' &&
             e.execution_id === event.execution_id &&
             e.messageId === messageId &&
             JSON.stringify((e as any).content) === newContent
      );
      if (existingIdx !== -1) {
        // True duplicate — update in place (e.g., final duration_ms)
        console.log('🔄 Updating duplicate llm_end event:', event.execution_id);
        const updated = [...eventsRef.current];
        updated[existingIdx] = eventWithFields;
        eventsRef.current = updated;
        setExecutionEvents([...eventsRef.current]);
        return;
      }
    }

    eventsRef.current = [...eventsRef.current, eventWithFields];
    setExecutionEvents([...eventsRef.current]);
  }, []);

  /**
   * Update the current assistant message with new parts
   */
  const updateAssistantMessage = useCallback((isComplete: boolean) => {
    const assistantId = currentAssistantIdRef.current;
    if (!assistantId) return;

    const parts = partsRef.current;

    // Build content - if no parts yet, show empty
    const content = parts.length > 0 ? [...parts] : [{ type: 'text' as const, text: '' }];

    console.log('📨 updateAssistantMessage - parts:', JSON.stringify(content.map(p => ({ type: p.type, text: p.type === 'text' ? (p as any).text?.substring(0, 100) : undefined, ...(p.type === 'tool-call' ? { toolName: (p as any).toolName, hasResult: !!(p as any).result } : {}) }))));


    const updatedMessage: ThreadMessageLike = {
      id: assistantId,
      role: 'assistant',
      content: content as ThreadAssistantMessagePart[],
      status: isComplete
        ? { type: 'complete', reason: 'stop' }
        : { type: 'running' },
    };

    setMessages(prev => {
      const idx = prev.findIndex(m => m.id === assistantId);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = updatedMessage;
        return updated;
      }
      return [...prev, updatedMessage];
    });
  }, []);

  /**
   * Add or update a text part in the current message
   * Uses currentTextPartIdxRef to track which text part to append to
   * After a tool call is added, currentTextPartIdxRef is reset so text creates a new part
   */
  const appendText = useCallback((text: string) => {
    const currentIdx = currentTextPartIdxRef.current;

    // If we have a valid current text part index, append to it
    if (currentIdx >= 0 && currentIdx < partsRef.current.length) {
      const existing = partsRef.current[currentIdx];
      if (existing.type === 'text') {
        existing.text += text;
        updateAssistantMessage(false);
        return;
      }
    }

    // Otherwise create a new text part
    const newIdx = partsRef.current.length;
    partsRef.current.push({ type: 'text', text });
    currentTextPartIdxRef.current = newIdx;
    updateAssistantMessage(false);
  }, [updateAssistantMessage]);

  /**
   * Add a tool call part
   * Resets currentTextPartIdxRef so subsequent text creates a new part (preserving interleaving)
   */
  const addToolCall = useCallback((toolCallId: string, toolName: string, args: Record<string, unknown>) => {
    partsRef.current.push({
      type: 'tool-call',
      toolCallId,
      toolName,
      args: args as JSONObject,
      argsText: JSON.stringify(args, null, 2),
    });
    // Reset text part index so next text creates a new part after this tool call
    currentTextPartIdxRef.current = -1;
    updateAssistantMessage(false);
  }, [updateAssistantMessage]);

  /**
   * Update a tool call with its result
   */
  const updateToolResult = useCallback((toolCallId: string, result: unknown, isError: boolean) => {
    const toolPart = partsRef.current.find(
      p => p.type === 'tool-call' && p.toolCallId === toolCallId
    ) as ToolCallPart | undefined;

    if (toolPart) {
      toolPart.result = result;
      toolPart.isError = isError;
    }
    updateAssistantMessage(false);
  }, [updateAssistantMessage]);

  /**
   * Handle sending a new message (with optional resource attachments)
   */
  const handleNewMessage = useCallback(async (userText: string, inputResources?: string[]) => {
    if (isRunning || !userText.trim()) return;

    setIsRunning(true);

    // Close existing WebSocket
    if (wsRef.current) {
      const oldWs = wsRef.current;
      await Promise.race([
        new Promise<void>(resolve => {
          if (oldWs.readyState === WebSocket.CLOSED || oldWs.readyState === WebSocket.CLOSING) {
            resolve();
          } else {
            oldWs.addEventListener('close', () => resolve(), { once: true });
            oldWs.close();
          }
        }),
        new Promise<void>(resolve => setTimeout(resolve, 5000))
      ]);
      wsRef.current = null;
    }

    // Add user message
    const userMessage: ThreadMessageLike = {
      id: generateId(),
      role: 'user',
      content: userText,
      createdAt: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Initialize assistant message
    currentAssistantIdRef.current = generateId();
    partsRef.current = [];
    currentTextPartIdxRef.current = -1;
    // Note: Don't clear execution events - keep history for the conversation
    // Events are only cleared on "New Chat" via clearMessages()

    // Add empty assistant message placeholder
    const assistantMessage: ThreadMessageLike = {
      id: currentAssistantIdRef.current,
      role: 'assistant',
      content: [{ type: 'text', text: '' }],
      status: { type: 'running' },
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      // Ensure conversation exists before starting agent (creates one if needed)
      // Skip if backend doesn't support conversation API (graceful degradation)
      let currentConversationId = conversationId;
      if (ensureConversation && !currentConversationId) {
        try {
          currentConversationId = await ensureConversation(userText);
          console.log('📝 Conversation ensured:', currentConversationId);
        } catch (convError) {
          console.warn('⚠️ Conversation API not available, proceeding without conversation tracking:', convError);
          // Continue without conversation - the agent will still work
        }
      }

      // Start agent with conversation tracking
      // For chat UI, always use 'conversation' context with conversation_id as root_execution_id
      // If user typed JSON, parse it into inputs so agents with InputSchema
      // can validate structured fields. prompt always keeps the raw text.
      let parsedInputs: Record<string, any> = {};
      try {
        const parsed = JSON.parse(userText);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedInputs = parsed;
        }
      } catch {
        // Not JSON — normal chat message, inputs stays empty
      }

      const agentRequest: Parameters<typeof apiClient.startAgent>[0] = {
        input: {
          prompt: userText,
          inputs: parsedInputs,
          context: {},
          files: [],
          streaming: false,
        },
        agent_id: defaultAgentId,  // agent_id accepts UUID (DB agents) or path (file agents)
        execution_context: 'conversation',
        root_execution_id: currentConversationId || '',  // Required for conversation context
      };

      // Always include identity fields - tenant_id is required for multi-tenancy
      // These values come from ConversationProvider which already handles defaults
      agentRequest.tenant_id = tenantId;
      agentRequest.project_id = projectId;
      agentRequest.user_id = userId;

      // Add input resources if provided (files already uploaded to resources service)
      if (inputResources && inputResources.length > 0) {
        agentRequest.input_resources = inputResources;
        console.log('📎 Starting agent with resources:', inputResources);
      }

      // Add configuration for agent execution
      const currentWorkingDirectory = workingDirectoryRef.current;

      // Check if this is an inline agent execution (Agent Builder)
      if (agentYaml) {
        // Inline agent execution - pass agent_yaml and prompt_md
        // Executor uses these instead of loading from disk/WFM
        agentRequest.agent_yaml = agentYaml;
        if (promptMd) {
          agentRequest.prompt_md = promptMd;
        }
        // Add resource bindings for LLM and MCP servers
        if (resourceBindings) {
          agentRequest.configuration = {
            ...extraConfiguration,
            bindings: resourceBindings,
          };
        }
        console.log('🔧 Starting inline agent:', {
          name: agentYaml.name,
          sdk: agentYaml.sdk,
          has_prompt: !!promptMd,
          has_bindings: !!resourceBindings,
          mcp_servers: agentYaml.mcp_servers?.length || 0,
        });
      } else if (basePath) {
        // File-based agent: pass base_path + optional skill_catalog + optional working_directory + optional environment_id + optional persona
        // Also merge any extra configuration passed through
        agentRequest.configuration = {
          ...extraConfiguration,
          base_path: basePath,
          ...(skillCatalog && { skill_catalog: skillCatalog }),
          ...(currentWorkingDirectory && { working_directory: currentWorkingDirectory }),
          ...(environmentId && { environment_id: environmentId }),
          ...(persona && { persona }),
        };
        console.log('📁 Starting file-based agent:', {
          base_path: basePath,
          skill_catalog_keys: skillCatalog ? Object.keys(skillCatalog) : [],
          working_directory: currentWorkingDirectory,
          persona,
        });
      } else {
        console.log('⚠️ [useEurekaRuntime] No basePath or agentYaml provided for agent:', defaultAgentId);
      }

      // DEBUG: Log the exact request being sent to the API
      console.log('🚀 [useEurekaRuntime] FINAL agentRequest:', JSON.stringify({
        agent_id: agentRequest.agent_id,
        agent_yaml: agentRequest.agent_yaml,
        prompt_md: agentRequest.prompt_md?.substring(0, 100),
        configuration: agentRequest.configuration,
      }, null, 2));

      const response = await apiClient.startAgent(agentRequest);

      // Store both IDs:
      // - root_execution_id for resume requests (conversation ID)
      // - agent_execution_id to know when this agent completes
      rootExecutionIdRef.current = response.root_execution_id;
      currentAgentExecutionIdRef.current = response.agent_execution_id;

      // Notify about execution start (for conversation tracking)
      if (onExecutionStarted) {
        onExecutionStarted(response.agent_execution_id, response.agent_type);
      }

      console.log('🚀 Agent started:', {
        execution_id: response.agent_execution_id,
        conversation_id: conversationId || 'none',
        agent_type: response.agent_type,
      });

      // Connect WebSocket using root_execution_id (this is the working URL format)
      const wsUrl = `/agent/${response.root_execution_id}/stream`;
      const ws = new WebSocket(apiClient.getWebSocketUrl(wsUrl));
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('🔌 WebSocket connected');
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('🔌 WS Event:', data.type, 'agent_execution_id:', data.agent_execution_id, 'parent:', data.parent_execution_id, data);

        if (data.type === 'llm_end') {
          // Add to execution log (with execution_id for nested agent support)
          addEvent({
            type: 'llm_end',
            execution_id: data.agent_execution_id,
            content: data.content || [],
            input_tokens: data.input_tokens || 0,
            output_tokens: data.output_tokens || 0,
            cost_usd: data.cost_usd || 0,
            duration_ms: data.duration_ms || 0,
            model: data.model || '',
            stop_reason: data.stop_reason || '',
          });

          // Process LLM response content blocks
          console.log('📝 LLM Response content:', data.content);
          for (const block of data.content) {
            if (block.type === 'text' && block.text) {
              appendText(block.text + '\n\n');
            } else if (block.type === 'tool_use') {
              console.log('🔧 Adding tool call from llm_response:', block.id, block.name);
              addToolCall(block.id, block.name, block.input || {});
            }
          }
          console.log('📦 Current parts after llm_response:', [...partsRef.current]);
        } else if (data.type === 'tool_call_started') {
          // Add to execution log (with execution_id for nested agent support)
          addEvent({
            type: 'tool_call_started',
            execution_id: data.agent_execution_id,
            tool_use_id: data.tool_use_id,
            tool_name: data.tool_name,
            tool_input: data.tool_input || {},
            is_async: data.is_async || false,
          });

          // Tool started - ensure it exists (may already be added from llm_response)
          const exists = partsRef.current.some(
            p => p.type === 'tool-call' && p.toolCallId === data.tool_use_id
          );
          if (!exists) {
            console.log('🔧 Adding tool call from tool_call_started:', data.tool_use_id, data.tool_name);
            addToolCall(data.tool_use_id, data.tool_name, data.tool_input || {});
          } else {
            console.log('🔧 Tool call already exists:', data.tool_use_id);
          }

          // Handle ask_user_permission - the permission request is in tool_input, not tool_result
          if (data.tool_name === 'ask_user_permission') {
            const input = data.tool_input || {};
            if (input.question) {
              console.log('⏸️ Detected ask_user_permission from tool_call_started:', input);
              setWaitingForUserInput({
                executionId: data.agent_execution_id,
                toolUseId: data.tool_use_id,
                question: input.question,
                options: input.options,
                inputType: input.input_type || 'confirmation',
                timeoutAt: input.timeout_at,
              });
              // Mark message as complete so send button is enabled
              updateAssistantMessage(true);
              setIsRunning(false);
            }
          }
        } else if (data.type === 'tool_call_completed') {
          // Add to execution log (with execution_id for nested agent support)
          addEvent({
            type: 'tool_call_completed',
            execution_id: data.agent_execution_id,
            tool_use_id: data.tool_use_id,
            tool_name: data.tool_name,
            result: data.result,
            is_error: data.is_error || false,
            duration_ms: data.duration_ms || 0,
          });

          // Tool completed
          console.log('✅ Tool result received:', data.tool_use_id, data.result);
          updateToolResult(data.tool_use_id, data.result, data.is_error || false);
          console.log('📦 Current parts after tool_result:', [...partsRef.current]);

          // WORKAROUND: Detect ask_user/ask_user_permission tool result with pending status
          // Backend should send waiting_for_user_input event, but as fallback we detect it here
          if (data.tool_name === 'ask_user' || data.tool_name === 'ask_user_permission') {
            const result = typeof data.result === 'string' ? JSON.parse(data.result) : data.result;
            if (result && result.status === 'pending' && result.question) {
              console.log('⏸️ Detected ask_user pending from tool_result (fallback):', result);
              setWaitingForUserInput({
                executionId: data.agent_execution_id,
                toolUseId: data.tool_use_id,
                question: result.question,
                options: result.options,
                inputType: result.input_type || 'free_text',
                timeoutAt: result.timeout_at,
                formSchema: result.form_schema,
                formValues: result.form_values,
                formAction: result.form_action,
                resourceType: result.resource_type,
                resourceId: result.resource_id,
              });
              // Mark message as complete so send button is enabled
              updateAssistantMessage(true);
              setIsRunning(false);
            }
          }
        } else if (data.type === 'subagent_started') {
          // Sub-agent started - add to execution log
          addEvent({
            type: 'subagent_started',
            execution_id: data.agent_execution_id,
            parent_execution_id: data.parent_execution_id,
            agent_type: data.agent_type || 'unknown',
            goal: data.goal || '',
          });
        } else if (data.type === 'subagent_completed') {
          // Sub-agent completed - add to execution log
          addEvent({
            type: 'subagent_completed',
            execution_id: data.agent_execution_id,
            parent_execution_id: data.parent_execution_id,
            duration_ms: data.duration_ms || 0,
            total_input_tokens: data.total_input_tokens || 0,
            total_output_tokens: data.total_output_tokens || 0,
            cost_usd: data.cost_usd || 0,
          });
        } else if (data.type === 'llm_start') {
          // ProCode subagent LLM start - add to execution log
          addEvent({
            type: 'llm_start',
            execution_id: data.agent_execution_id,
            agent_name: data.agent_name || '',
          });
        } else if (data.type === 'llm_end') {
          // ProCode subagent LLM end - add to execution log
          addEvent({
            type: 'llm_end',
            execution_id: data.agent_execution_id,
            agent_name: data.agent_name || '',
            input_tokens: data.input_tokens || 0,
            output_tokens: data.output_tokens || 0,
            cost_usd: data.cost_usd || 0,
          });
        } else if (data.type === 'agent_start') {
          // ProCode subagent agent start - add to execution log
          addEvent({
            type: 'agent_start',
            execution_id: data.agent_execution_id,
            agent_name: data.agent_name || '',
          });
        } else if (data.type === 'tool_progress') {
          // Tool progress update - add to execution log (updates in place via lookup)
          console.log('📊 Tool progress:', data.tool_call_id, data.progress);
          addEvent({
            type: 'tool_progress',
            execution_id: data.agent_execution_id,
            tool_call_id: data.tool_call_id,
            tool_name: data.tool_name,
            progress: data.progress,
          });
        } else if (data.type === 'execution_completed') {
          // Add to execution log (with execution_id for nested agent support)
          addEvent({
            type: 'execution_completed',
            execution_id: data.agent_execution_id,
            duration_ms: data.duration_ms || 0,
            total_input_tokens: data.total_input_tokens || 0,
            total_output_tokens: data.total_output_tokens || 0,
            cost_usd: data.cost_usd || 0,
          });

          // Only close WebSocket when ROOT agent completes, not subagents
          // This allows us to receive all events from the entire agent chain
          const isRootAgent = !data.agent_execution_id || data.agent_execution_id === currentAgentExecutionIdRef.current;
          console.log('✅ Agent completed:', {
            agent_execution_id: data.agent_execution_id,
            currentAgentExecutionId: currentAgentExecutionIdRef.current,
            isRoot: isRootAgent,
            duration: data.duration_ms,
            tokens: `${data.total_input_tokens} in, ${data.total_output_tokens} out`,
            cost: data.cost_usd
          });

          if (isRootAgent) {
            console.log('📦 Final parts before completion:', [...partsRef.current]);
            updateAssistantMessage(true);
            setIsRunning(false);
            currentAssistantIdRef.current = null;
            rootExecutionIdRef.current = null;
            currentAgentExecutionIdRef.current = null;
            ws.close();
          }
        } else if (data.type === 'tools_finder_started') {
          addEvent({
            type: 'tools_finder_started',
            execution_id: data.agent_execution_id,
            unavailable_tools: data.unavailable_tools || [],
            count: data.count || 0,
          });
        } else if (data.type === 'tools_finder_completed') {
          addEvent({
            type: 'tools_finder_completed',
            execution_id: data.agent_execution_id,
            total_tools: data.total_tools || 0,
            alternatives_found: data.alternatives_found || 0,
            results: data.results || {},
          });
        } else if (data.type === 'waiting_for_user_input') {
          // Agent is waiting for user input (ask_user tool)
          console.log('⏸️ Waiting for user input:', data);
          setWaitingForUserInput({
            executionId: data.agent_execution_id,
            toolUseId: data.tool_use_id,
            question: data.question,
            options: data.options,
            inputType: data.input_type || 'free_text',
            timeoutAt: data.timeout_at,
            // Resource form fields (for input_type="resource_form")
            formSchema: data.form_schema,
            formValues: data.form_values,
            formAction: data.form_action,
            resourceType: data.resource_type,
            resourceId: data.resource_id,
          });
          // Mark message as complete so send button is enabled
          // The message will be marked as running again when user responds
          updateAssistantMessage(true);
          setIsRunning(false);
        } else if (data.type === 'execution_failed') {
          // Agent execution failed — only close WebSocket for ROOT agent failures
          // Subagent failures are reported via subagent_failed events
          const isRootAgent = !data.agent_execution_id || data.agent_execution_id === currentAgentExecutionIdRef.current;
          console.error('❌ Agent failed:', {
            agent_execution_id: data.agent_execution_id,
            currentAgentExecutionId: currentAgentExecutionIdRef.current,
            isRoot: isRootAgent,
            error: data.error,
          });

          if (isRootAgent) {
            const errorMsg = data.error || data.message || data.reason || 'Agent execution failed';
            const errorDetail = data.error_detail ? `\n\n\`\`\`\n${data.error_detail}\n\`\`\`` : '';
            const hasContent = partsRef.current.length > 0 && partsRef.current.some(p => p.type === 'text' && (p as any).text?.trim());
            const prefix = hasContent ? '\n\n' : '';
            const errorText = `${prefix}❌ **Error:** ${errorMsg}${errorDetail}`;
            appendText(errorText);
            updateAssistantMessage(true);
            setIsRunning(false);
            currentAssistantIdRef.current = null;
            ws.close();
          } else {
            // Subagent failed — don't close WebSocket, parent will resume
            console.log('⚠️ Subagent failed, parent will resume:', data.agent_execution_id);
          }
        } else if (data.type === 'error') {
          // Error event — only close WebSocket for root agent errors
          const isRootAgent3 = !data.agent_execution_id || data.agent_execution_id === currentAgentExecutionIdRef.current;
          console.error('❌ Agent error:', { error: data.error, isRoot: isRootAgent3 });

          if (isRootAgent3) {
            const hasContent2 = partsRef.current.length > 0 && partsRef.current.some(p => p.type === 'text' && (p as any).text?.trim());
            const prefix2 = hasContent2 ? '\n\n' : '';
            appendText(`${prefix2}❌ **Error:** ${data.error || data.message || 'Unknown error'}`);
            updateAssistantMessage(true);
            setIsRunning(false);
            currentAssistantIdRef.current = null;
            ws.close();
          } else {
            console.log('⚠️ Subagent error, parent will handle:', data.agent_execution_id);
          }
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        appendText('\n\n❌ Connection error');
        updateAssistantMessage(true);
        setIsRunning(false);
        currentAssistantIdRef.current = null;
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
        wsRef.current = null;
        setIsConnected(false);
        // Skip cleanup if this is an intentional close (for resume after ask_user)
        if (isIntentionalCloseRef.current) {
          console.log('🔌 Intentional close for resume, skipping cleanup');
          isIntentionalCloseRef.current = false;
          return;
        }
        // Clean up if we still have an active assistant message (abnormal close)
        // Note: Normal close after agent_completed already sets currentAssistantIdRef to null
        if (currentAssistantIdRef.current) {
          console.warn('⚠️ WebSocket closed with active message, cleaning up');
          updateAssistantMessage(true);
          setIsRunning(false);
          currentAssistantIdRef.current = null;
        }
      };

    } catch (error) {
      console.error('❌ Failed to start agent:', error);
      appendText(`❌ Failed to start agent: ${error instanceof Error ? error.message : 'Unknown error'}`);
      updateAssistantMessage(true);
      setIsRunning(false);
      currentAssistantIdRef.current = null;
    }
  }, [isRunning, appendText, addToolCall, updateToolResult, updateAssistantMessage, conversationId, ensureConversation, onExecutionStarted, tenantId, projectId, userId, defaultAgentId, basePath, skillCatalog, workingDirectory, environmentId, persona, extraConfiguration]);

  /**
   * Submit user response to resume agent execution (ask_user feature)
   * Calls /agent/start with same root_execution_id and user's answer as goal
   */
  const submitUserResponse = useCallback(async (response: string) => {
    if (!waitingForUserInput) {
      console.warn('⚠️ submitUserResponse called but not waiting for input');
      return;
    }

    const { executionId, toolUseId } = waitingForUserInput;
    console.log('📤 Submitting user response:', { executionId, toolUseId, response });

    // Update the ask_user tool result with the user's response
    updateToolResult(toolUseId, response, false);

    // Add tool_result event to execution log so it shows as complete
    addEvent({
      type: 'tool_call_completed',
      execution_id: executionId,
      tool_use_id: toolUseId,
      tool_name: 'ask_user',
      result: response,
      is_error: false,
      duration_ms: 0,
    });

    // Clear waiting state and mark message as running again
    setWaitingForUserInput(null);
    setIsRunning(true);
    updateAssistantMessage(false);  // Mark as running while we continue

    // Close existing WebSocket if any (mark as intentional to skip cleanup)
    if (wsRef.current) {
      isIntentionalCloseRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      // Resume agent with user's response
      // Use the same root_execution_id to continue the conversation
      const rootId = rootExecutionIdRef.current || executionId;
      console.log('📤 Resume request - rootExecutionIdRef:', rootExecutionIdRef.current, 'executionId fallback:', executionId, 'using:', rootId);

      const agentRequest: Parameters<typeof apiClient.startAgent>[0] = {
        input: {
          prompt: 'Continue execution with user response',  // Placeholder prompt for resume
          inputs: {},
          context: {},
          files: [],
          streaming: false,
        },
        agent_id: defaultAgentId,  // agent_id accepts UUID (DB agents) or path (file agents)
        execution_context: 'conversation',
        root_execution_id: rootId,
        tool_use_id: toolUseId,  // Include tool_use_id so backend knows which ask_user to answer
        user_response: response,  // The actual user response - backend should store as tool_result
      };

      // Always include identity fields - tenant_id is required for multi-tenancy
      // These values come from ConversationProvider which already handles defaults
      agentRequest.tenant_id = tenantId;
      agentRequest.project_id = projectId;
      agentRequest.user_id = userId;

      // Add file-based configuration for resume
      // This is required for resume to work with file-based agents
      // Also merge any extra configuration passed through
      if (basePath) {
        agentRequest.configuration = {
          ...extraConfiguration,
          base_path: basePath,
          ...(skillCatalog && { skill_catalog: skillCatalog }),
          ...(workingDirectory && { working_directory: workingDirectory }),
          ...(persona && { persona }),
        };
      }

      const startResponse = await apiClient.startAgent(agentRequest);
      console.log('🔄 Agent resumed:', {
        response: startResponse,
        currentAgentExecutionId: currentAgentExecutionIdRef.current,
        rootExecutionId: rootExecutionIdRef.current,
        resumedAgentExecutionId: startResponse.agent_execution_id,
      });

      // DON'T update currentAgentExecutionIdRef on resume!
      // The agent_execution_id in the resume response is the subagent that was resumed,
      // not the root agent. We need to keep tracking the root agent's execution_id
      // so that agent_completed events from subagents don't close the WebSocket prematurely.
      // Only root agent completion should close the WebSocket.
      //
      // The root_execution_id is the stable conversation identifier used for WebSocket connection.

      // Update root_execution_id if returned (should stay the same, but be safe)
      if (startResponse.root_execution_id) {
        rootExecutionIdRef.current = startResponse.root_execution_id;
      }

      // Reconnect WebSocket to continue streaming
      const wsUrl = `/agent/${startResponse.root_execution_id}/stream`;
      const ws = new WebSocket(apiClient.getWebSocketUrl(wsUrl));
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('🔌 WebSocket connected (resumed)');
        setIsConnected(true);
      };

      // Re-use same WebSocket handlers as handleNewMessage
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('🔌 WS Event (resumed):', data.type, 'agent_execution_id:', data.agent_execution_id, 'parent:', data.parent_execution_id, data);

        if (data.type === 'llm_end') {
          addEvent({
            type: 'llm_end',
            execution_id: data.agent_execution_id,
            content: data.content || [],
            input_tokens: data.input_tokens || 0,
            output_tokens: data.output_tokens || 0,
            cost_usd: data.cost_usd || 0,
            duration_ms: data.duration_ms || 0,
            model: data.model || '',
            stop_reason: data.stop_reason || '',
          });

          for (const block of data.content) {
            if (block.type === 'text' && block.text) {
              appendText(block.text + '\n\n');
            } else if (block.type === 'tool_use') {
              addToolCall(block.id, block.name, block.input || {});
            }
          }
        } else if (data.type === 'tool_call_started') {
          addEvent({
            type: 'tool_call_started',
            execution_id: data.agent_execution_id,
            tool_use_id: data.tool_use_id,
            tool_name: data.tool_name,
            tool_input: data.tool_input || {},
            is_async: data.is_async || false,
          });

          const exists = partsRef.current.some(
            p => p.type === 'tool-call' && p.toolCallId === data.tool_use_id
          );
          if (!exists) {
            addToolCall(data.tool_use_id, data.tool_name, data.tool_input || {});
          }

          // Handle ask_user_permission - the permission request is in tool_input (resumed handler)
          if (data.tool_name === 'ask_user_permission') {
            const input = data.tool_input || {};
            if (input.question) {
              console.log('⏸️ Detected ask_user_permission from tool_call_started (resumed):', input);
              setWaitingForUserInput({
                executionId: data.agent_execution_id,
                toolUseId: data.tool_use_id,
                question: input.question,
                options: input.options,
                inputType: input.input_type || 'confirmation',
                timeoutAt: input.timeout_at,
              });
              // Mark message as complete so send button is enabled
              updateAssistantMessage(true);
              setIsRunning(false);
            }
          }
        } else if (data.type === 'tool_call_completed') {
          addEvent({
            type: 'tool_call_completed',
            execution_id: data.agent_execution_id,
            tool_use_id: data.tool_use_id,
            tool_name: data.tool_name,
            result: data.result,
            is_error: data.is_error || false,
            duration_ms: data.duration_ms || 0,
          });
          updateToolResult(data.tool_use_id, data.result, data.is_error || false);

          // WORKAROUND: Detect ask_user/ask_user_permission tool result with pending status (resumed handler)
          // Backend should send waiting_for_user_input event, but as fallback we detect it here
          if (data.tool_name === 'ask_user' || data.tool_name === 'ask_user_permission') {
            const result = typeof data.result === 'string' ? JSON.parse(data.result) : data.result;
            if (result && result.status === 'pending' && result.question) {
              console.log('⏸️ Detected ask_user pending from tool_result (resumed, fallback):', result);
              setWaitingForUserInput({
                executionId: data.agent_execution_id,
                toolUseId: data.tool_use_id,
                question: result.question,
                options: result.options,
                inputType: result.input_type || 'free_text',
                timeoutAt: result.timeout_at,
                formSchema: result.form_schema,
                formValues: result.form_values,
                formAction: result.form_action,
                resourceType: result.resource_type,
                resourceId: result.resource_id,
              });
              // Mark message as complete so send button is enabled
              updateAssistantMessage(true);
              setIsRunning(false);
            }
          }
        } else if (data.type === 'subagent_started') {
          addEvent({
            type: 'subagent_started',
            execution_id: data.agent_execution_id,
            parent_execution_id: data.parent_execution_id,
            agent_type: data.agent_type || 'unknown',
            goal: data.goal || '',
          });
        } else if (data.type === 'subagent_completed') {
          addEvent({
            type: 'subagent_completed',
            execution_id: data.agent_execution_id,
            parent_execution_id: data.parent_execution_id,
            duration_ms: data.duration_ms || 0,
            total_input_tokens: data.total_input_tokens || 0,
            total_output_tokens: data.total_output_tokens || 0,
            cost_usd: data.cost_usd || 0,
          });
        } else if (data.type === 'llm_start') {
          // ProCode subagent LLM start (resumed)
          addEvent({
            type: 'llm_start',
            execution_id: data.agent_execution_id,
            agent_name: data.agent_name || '',
          });
        } else if (data.type === 'llm_end') {
          // ProCode subagent LLM end (resumed)
          addEvent({
            type: 'llm_end',
            execution_id: data.agent_execution_id,
            agent_name: data.agent_name || '',
            input_tokens: data.input_tokens || 0,
            output_tokens: data.output_tokens || 0,
            cost_usd: data.cost_usd || 0,
          });
        } else if (data.type === 'agent_start') {
          // ProCode subagent agent start (resumed)
          addEvent({
            type: 'agent_start',
            execution_id: data.agent_execution_id,
            agent_name: data.agent_name || '',
          });
        } else if (data.type === 'tool_progress') {
          // Tool progress update (resumed)
          console.log('📊 Tool progress (resumed):', data.tool_call_id, data.progress);
          addEvent({
            type: 'tool_progress',
            execution_id: data.agent_execution_id,
            tool_call_id: data.tool_call_id,
            tool_name: data.tool_name,
            progress: data.progress,
          });
        } else if (data.type === 'execution_completed') {
          addEvent({
            type: 'execution_completed',
            execution_id: data.agent_execution_id,
            duration_ms: data.duration_ms || 0,
            total_input_tokens: data.total_input_tokens || 0,
            total_output_tokens: data.total_output_tokens || 0,
            cost_usd: data.cost_usd || 0,
          });

          const isRootAgent = !data.agent_execution_id || data.agent_execution_id === currentAgentExecutionIdRef.current;
          console.log('✅ Agent completed (resumed):', {
            agent_execution_id: data.agent_execution_id,
            tracked_execution_id: currentAgentExecutionIdRef.current,
            isRootAgent,
            match_reason: !data.agent_execution_id ? 'no_agent_execution_id_in_event' :
                          (data.agent_execution_id === currentAgentExecutionIdRef.current ? 'ids_match' : 'ids_mismatch'),
          });
          if (isRootAgent) {
            updateAssistantMessage(true);
            setIsRunning(false);
            currentAssistantIdRef.current = null;
            rootExecutionIdRef.current = null;
            currentAgentExecutionIdRef.current = null;
            ws.close();
          }
        } else if (data.type === 'tools_finder_started') {
          addEvent({
            type: 'tools_finder_started',
            execution_id: data.agent_execution_id,
            unavailable_tools: data.unavailable_tools || [],
            count: data.count || 0,
          });
        } else if (data.type === 'tools_finder_completed') {
          addEvent({
            type: 'tools_finder_completed',
            execution_id: data.agent_execution_id,
            total_tools: data.total_tools || 0,
            alternatives_found: data.alternatives_found || 0,
            results: data.results || {},
          });
        } else if (data.type === 'waiting_for_user_input') {
          console.log('⏸️ Waiting for user input (again):', data);
          setWaitingForUserInput({
            executionId: data.agent_execution_id,
            toolUseId: data.tool_use_id,
            question: data.question,
            options: data.options,
            inputType: data.input_type || 'free_text',
            timeoutAt: data.timeout_at,
            // Resource form fields (for input_type="resource_form")
            formSchema: data.form_schema,
            formValues: data.form_values,
            formAction: data.form_action,
            resourceType: data.resource_type,
            resourceId: data.resource_id,
          });
          // Mark message as complete so send button is enabled
          updateAssistantMessage(true);
          setIsRunning(false);
        } else if (data.type === 'execution_failed') {
          // Agent execution failed — only close WebSocket for ROOT agent failures
          const isRootAgent = !data.agent_execution_id || data.agent_execution_id === currentAgentExecutionIdRef.current;
          console.error('❌ Agent failed (resumed):', {
            agent_execution_id: data.agent_execution_id,
            currentAgentExecutionId: currentAgentExecutionIdRef.current,
            isRoot: isRootAgent,
            error: data.error,
          });

          if (isRootAgent) {
            const errorMsg = data.error || data.message || data.reason || 'Agent execution failed';
            const errorDetail = data.error_detail ? `\n\n\`\`\`\n${data.error_detail}\n\`\`\`` : '';
            const hasContent = partsRef.current.length > 0 && partsRef.current.some(p => p.type === 'text' && (p as any).text?.trim());
            const prefix = hasContent ? '\n\n' : '';
            appendText(`${prefix}❌ **Error:** ${errorMsg}${errorDetail}`);
            updateAssistantMessage(true);
            setIsRunning(false);
            currentAssistantIdRef.current = null;
            ws.close();
          } else {
            console.log('⚠️ Subagent failed (resumed), parent will resume:', data.agent_execution_id);
          }
        } else if (data.type === 'error') {
          const isRootAgent3 = !data.agent_execution_id || data.agent_execution_id === currentAgentExecutionIdRef.current;
          console.error('❌ Agent error (resumed):', { error: data.error, isRoot: isRootAgent3 });

          if (isRootAgent3) {
            const hasContent = partsRef.current.length > 0 && partsRef.current.some(p => p.type === 'text' && (p as any).text?.trim());
            const prefix = hasContent ? '\n\n' : '';
            appendText(`${prefix}❌ **Error:** ${data.error || data.message || 'Unknown error'}`);
            updateAssistantMessage(true);
            setIsRunning(false);
            currentAssistantIdRef.current = null;
            ws.close();
          } else {
            console.log('⚠️ Subagent error (resumed), parent will handle:', data.agent_execution_id);
          }
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error (resumed):', error);
        appendText('\n\n❌ Connection error');
        updateAssistantMessage(true);
        setIsRunning(false);
        currentAssistantIdRef.current = null;
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket closed (resumed):', { code: event.code, reason: event.reason, wasClean: event.wasClean });
        wsRef.current = null;
        // Skip cleanup if this is an intentional close (for another resume after ask_user)
        if (isIntentionalCloseRef.current) {
          console.log('🔌 Intentional close for resume, skipping cleanup');
          isIntentionalCloseRef.current = false;
          return;
        }
        // Clean up if we still have an active assistant message (abnormal close)
        if (currentAssistantIdRef.current) {
          console.warn('⚠️ WebSocket closed (resumed) with active message, cleaning up');
          updateAssistantMessage(true);
          setIsRunning(false);
          currentAssistantIdRef.current = null;
        }
      };

    } catch (error) {
      console.error('❌ Failed to resume agent:', error);
      appendText(`\n\n❌ Failed to resume: ${error instanceof Error ? error.message : 'Unknown error'}`);
      updateAssistantMessage(true);
      setIsRunning(false);
    }
  }, [waitingForUserInput, defaultAgentId, tenantId, projectId, userId, addEvent, appendText, addToolCall, updateToolResult, updateAssistantMessage]);

  /**
   * Handle cancel - stops the agent execution via backend API
   */
  const handleCancel = useCallback(async () => {
    // Call backend stop API if we have an active execution
    const rootExecutionId = rootExecutionIdRef.current;
    if (rootExecutionId) {
      try {
        console.log('🛑 Stopping agent execution:', rootExecutionId);
        await apiClient.stopAgent(rootExecutionId);
        console.log('✅ Stop request sent successfully');
      } catch (error) {
        // Log but don't fail - agent may have already completed
        console.warn('⚠️ Stop request failed (agent may have completed):', error);
      }
    }

    // Mark as intentional close to prevent duplicate cleanup in onclose handler
    isIntentionalCloseRef.current = true;

    // Close WebSocket connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    appendText('\n\n⚠️ Stopped');
    updateAssistantMessage(true);
    setIsRunning(false);
    currentAssistantIdRef.current = null;
  }, [appendText, updateAssistantMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  /**
   * Clear messages to start a new chat
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    partsRef.current = [];
    currentTextPartIdxRef.current = -1;
    eventsRef.current = [];
    setExecutionEvents([]);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsRunning(false);
    currentAssistantIdRef.current = null;
  }, []);

  // Refs to avoid stale closures in runtime callbacks
  // The runtime's onNew/onReload callbacks capture these refs, not the functions directly
  // This ensures they always call the latest version of the callbacks
  const handleNewMessageRef = useRef(handleNewMessage);
  handleNewMessageRef.current = handleNewMessage;
  const handleCancelRef = useRef(handleCancel);
  handleCancelRef.current = handleCancel;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const waitingForUserInputRef = useRef(waitingForUserInput);
  waitingForUserInputRef.current = waitingForUserInput;
  const submitUserResponseRef = useRef(submitUserResponse);
  submitUserResponseRef.current = submitUserResponse;

  // Create stable sendMessage wrapper that always calls the latest handleNewMessage via ref
  // This ensures consumers get a stable function reference that never changes across renders,
  // preventing issues like useEffect retry loops when sendMessage is in dependency arrays
  const sendMessage = useCallback(
    (text: string, inputResources?: string[]) => {
      return handleNewMessageRef.current(text, inputResources);
    },
    [] // Empty deps = stable reference forever
  );

  // Create runtime
  const runtime = useExternalStoreRuntime({
    messages,
    isRunning,

    // Identity converter since messages are already ThreadMessageLike
    convertMessage: (msg: ThreadMessageLike) => msg,

    // Enable attachment support - uploads files to resources service immediately
    adapters: {
      attachments: {
        accept: '*/*', // Accept all file types
        async add({ file }) {
          const attachmentId = `attachment-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

          try {
            // Upload to resources service immediately
            console.log('📤 Uploading file to resources:', file.name);
            const response = await apiClient.uploadResource(file, {
              tenant_id: tenantId || getIdentity().tenantId,
              resource_type: 'agent_input',
            });

            console.log('✅ File uploaded, resource_id:', response.resource_id);

            // Store the mapping in the ref (survives library's internal transformations)
            uploadedResourcesRef.current.set(attachmentId, response.resource_id);
            console.log('📝 Stored resource mapping:', attachmentId, '->', response.resource_id);

            // Return PendingAttachment with resourceId stored for later use
            // Must include file and use requires-action status per PendingAttachment type
            return {
              id: attachmentId,
              type: file.type.startsWith('image/') ? 'image' as const : 'file' as const,
              name: file.name,
              contentType: file.type,
              file, // Required by PendingAttachment type
              resourceId: response.resource_id, // Store resource ID for agent request
              status: { type: 'requires-action' as const, reason: 'composer-send' as const },
            };
          } catch (error) {
            console.error('❌ File upload failed:', error);
            // Return error state - user can retry by removing and re-adding
            return {
              id: attachmentId,
              type: file.type.startsWith('image/') ? 'image' as const : 'file' as const,
              name: file.name,
              contentType: file.type,
              file, // Required by PendingAttachment type
              status: { type: 'requires-action' as const, reason: 'composer-send' as const },
              uploadError: error instanceof Error ? error.message : 'Upload failed',
            };
          }
        },
        async remove() {
          // Optional: could delete from resources service
          // For now, orphaned uploads will be cleaned up by backend TTL
        },
        async send(attachment) {
          // Transform to CompleteAttachment with resourceId
          return {
            id: attachment.id,
            type: attachment.type,
            name: attachment.name,
            contentType: attachment.contentType,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            resourceId: (attachment as any).resourceId, // Pass resourceId to message
            status: { type: 'complete' as const },
            content: [],
          };
        },
      },
    },

    onNew: async (message) => {
      // Extract text from the message
      const text = typeof message.content === 'string'
        ? message.content
        : message.content
            .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
            .map(p => p.text)
            .join('\n');

      // Extract resourceIds from attachments using the ref (library may strip custom props)
      const inputResources: string[] = [];
      if (message.attachments && Array.isArray(message.attachments)) {
        console.log('📎 Processing attachments:', message.attachments.length);
        for (const attachment of message.attachments) {
          // First try the ref lookup (most reliable)
          const resourceIdFromRef = uploadedResourcesRef.current.get(attachment.id);
          // Fallback to custom property (in case it wasn't stripped)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const resourceIdFromProp = (attachment as any).resourceId;

          const resourceId = resourceIdFromRef || resourceIdFromProp;
          console.log('📎 Attachment:', attachment.id, '-> resourceId:', resourceId, '(ref:', resourceIdFromRef, ', prop:', resourceIdFromProp, ')');

          if (resourceId) {
            inputResources.push(resourceId);
          }
        }
      }

      console.log('📎 Final input_resources:', inputResources.length, 'resources', inputResources);

      // If waiting for user input, route to submitUserResponse instead of starting new agent
      if (waitingForUserInputRef.current) {
        console.log('📝 Routing chat input to submitUserResponse');
        await submitUserResponseRef.current(text);
        return;
      }

      // Use ref to always get latest handleNewMessage (avoids stale closure)
      await handleNewMessageRef.current(text, inputResources.length > 0 ? inputResources : undefined);
    },

    onCancel: () => handleCancelRef.current(),

    onReload: async () => {
      // Use ref to get latest messages (avoids stale closure)
      const currentMessages = messagesRef.current;
      const lastUser = [...currentMessages].reverse().find(m => m.role === 'user');
      if (lastUser) {
        const text = typeof lastUser.content === 'string'
          ? lastUser.content
          : (lastUser.content as MessagePart[])
              .filter((p): p is TextPart => p.type === 'text')
              .map(p => p.text)
              .join('\n');

        // Remove last assistant message
        setMessages(prev => {
          const lastIdx = prev.length - 1;
          if (prev[lastIdx]?.role === 'assistant') {
            return prev.slice(0, -1);
          }
          return prev;
        });

        // Use ref to always get latest handleNewMessage
        await handleNewMessageRef.current(text);
      }
    },
  });

  /**
   * Inject mock events for testing nested sub-agent rendering
   * Call this from browser console: window.__injectMockNestedEvents()
   */
  const injectMockNestedEvents = useCallback(() => {
    const baseTime = Date.now();
    const mockEvents = [
      // Root agent starts
      {
        type: 'llm_end' as const,
        execution_id: 'root-001',
        content: [{ type: 'text', text: 'Let me research this topic for you. I\'ll spawn a research sub-agent.' }],
        input_tokens: 100,
        output_tokens: 30,
        cost_usd: 0.002,
        duration_ms: 800,
        model: 'claude-sonnet-4-20250514',
        stop_reason: 'tool_use',
      },
      // Sub-agent 1 starts
      {
        type: 'subagent_started' as const,
        execution_id: 'sub-001',
        parent_execution_id: 'root-001',
        agent_type: 'researcher',
        goal: 'Research quantum computing applications',
      },
      // Sub-agent 1 LLM response
      {
        type: 'llm_end' as const,
        execution_id: 'sub-001',
        content: [{ type: 'text', text: 'Searching for quantum computing information...' }],
        input_tokens: 80,
        output_tokens: 15,
        cost_usd: 0.001,
        duration_ms: 600,
        model: 'claude-sonnet-4-20250514',
        stop_reason: 'tool_use',
      },
      // Sub-agent 1 tool call
      {
        type: 'tool_call_started' as const,
        execution_id: 'sub-001',
        tool_use_id: 'tool-001',
        tool_name: 'web_search',
        tool_input: { query: 'quantum computing 2024 applications' },
        is_async: false,
      },
      // Sub-agent 1 tool result
      {
        type: 'tool_call_completed' as const,
        execution_id: 'sub-001',
        tool_use_id: 'tool-001',
        tool_name: 'web_search',
        result: { results: ['Result 1: Quantum computing in drug discovery', 'Result 2: Financial modeling'] },
        is_error: false,
        duration_ms: 1200,
      },
      // Nested sub-agent (sub-sub-agent)
      {
        type: 'subagent_started' as const,
        execution_id: 'sub-002',
        parent_execution_id: 'sub-001',
        agent_type: 'summarizer',
        goal: 'Summarize search results',
      },
      // Sub-sub-agent LLM
      {
        type: 'llm_end' as const,
        execution_id: 'sub-002',
        content: [{ type: 'text', text: 'Summarizing the research findings...' }],
        input_tokens: 200,
        output_tokens: 50,
        cost_usd: 0.003,
        duration_ms: 900,
        model: 'claude-sonnet-4-20250514',
        stop_reason: 'end_turn',
      },
      // Sub-sub-agent completes
      {
        type: 'subagent_completed' as const,
        execution_id: 'sub-002',
        parent_execution_id: 'sub-001',
        duration_ms: 1100,
        total_input_tokens: 200,
        total_output_tokens: 50,
        cost_usd: 0.003,
      },
      // Sub-agent 1 continues after nested agent
      {
        type: 'llm_end' as const,
        execution_id: 'sub-001',
        content: [{ type: 'text', text: 'Research complete. Found relevant information.' }],
        input_tokens: 300,
        output_tokens: 80,
        cost_usd: 0.004,
        duration_ms: 1000,
        model: 'claude-sonnet-4-20250514',
        stop_reason: 'end_turn',
      },
      // Sub-agent 1 completes
      {
        type: 'subagent_completed' as const,
        execution_id: 'sub-001',
        parent_execution_id: 'root-001',
        duration_ms: 4500,
        total_input_tokens: 580,
        total_output_tokens: 145,
        cost_usd: 0.008,
      },
      // Root agent continues
      {
        type: 'llm_end' as const,
        execution_id: 'root-001',
        content: [{ type: 'text', text: 'Based on my research, quantum computing has several key applications including drug discovery and financial modeling.' }],
        input_tokens: 400,
        output_tokens: 100,
        cost_usd: 0.005,
        duration_ms: 1200,
        model: 'claude-sonnet-4-20250514',
        stop_reason: 'end_turn',
      },
      // Root agent completes
      {
        type: 'execution_completed' as const,
        execution_id: 'root-001',
        duration_ms: 7000,
        total_input_tokens: 1080,
        total_output_tokens: 275,
        cost_usd: 0.015,
      },
    ];

    // Find the last assistant message ID to attach events to
    // Use currentAssistantIdRef if running, otherwise find last assistant message
    let messageId = currentAssistantIdRef.current;
    if (!messageId) {
      // Find the last assistant message from the messages array
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
      messageId = lastAssistant?.id || 'mock-message';
    }

    // Clear existing events for this message and inject mock events
    eventsRef.current = eventsRef.current.filter(e => e.messageId !== messageId);
    mockEvents.forEach((event, idx) => {
      const eventWithFields = {
        ...event,
        timestamp: baseTime + (idx * 500), // 500ms apart
        messageId,
      } as ExecutionEvent;
      eventsRef.current.push(eventWithFields);
    });
    setExecutionEvents([...eventsRef.current]);
    console.log('🧪 Injected mock nested events for message:', messageId, 'count:', mockEvents.length);
  }, [messages]);

  // Expose mock injection on window for console testing (dev only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__injectMockNestedEvents = injectMockNestedEvents;
      (window as any).__addEvent = addEvent;
      console.log('🧪 Mock testing available: window.__injectMockNestedEvents() or window.__addEvent({...})');
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__injectMockNestedEvents;
        delete (window as any).__addEvent;
      }
    };
  }, [injectMockNestedEvents, addEvent]);

  /**
   * Inject a test assistant message with arbitrary content
   * Used for testing UI components like ResourceListMessage, ResourceFormMessage
   */
  const injectTestMessage = useCallback((content: string) => {
    const testMessageId = generateId();
    const testMessage: ThreadMessageLike = {
      id: testMessageId,
      role: 'assistant',
      content: [{ type: 'text', text: content }],
      status: { type: 'complete', reason: 'stop' },
    };
    setMessages(prev => [...prev, testMessage]);
    console.log('🧪 Injected test message:', testMessageId);
    return testMessageId;
  }, []);

  // Expose test injection on window for console testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__injectTestMessage = injectTestMessage;
      console.log('🧪 Test injection available: window.__injectTestMessage(jsonString)');
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__injectTestMessage;
      }
    };
  }, [injectTestMessage]);

  /**
   * Reconnect to an active execution's WebSocket stream
   * Used when returning to a conversation that has an in-progress execution
   * Does NOT start a new agent - only reconnects to existing stream
   */
  const reconnectToExecution = useCallback(async (rootExecutionId: string) => {
    if (wsRef.current) {
      console.log('🔌 Already connected, skipping reconnect');
      return;
    }

    console.log('🔄 Reconnecting to execution:', rootExecutionId);
    rootExecutionIdRef.current = rootExecutionId;
    // For reconnect, the root execution ID is also the agent execution ID we're tracking
    // This is needed so agent_completed events properly trigger cleanup
    currentAgentExecutionIdRef.current = rootExecutionId;
    setIsRunning(true);

    // Find the last assistant message to continue appending to
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (lastAssistant) {
      currentAssistantIdRef.current = lastAssistant.id ?? null;
      // Restore parts from message content
      const content = lastAssistant.content;
      if (Array.isArray(content)) {
        partsRef.current = content.map(part => {
          if (part.type === 'text') {
            return { type: 'text' as const, text: (part as any).text || '' };
          } else if (part.type === 'tool-call') {
            return {
              type: 'tool-call' as const,
              toolCallId: (part as any).toolCallId,
              toolName: (part as any).toolName,
              args: (part as any).args || {},
              argsText: (part as any).argsText || '',
              result: (part as any).result,
              isError: (part as any).isError,
            };
          }
          return { type: 'text' as const, text: '' };
        });
        currentTextPartIdxRef.current = partsRef.current.length - 1;
      }
    }

    // Connect to WebSocket stream
    const wsUrl = `/agent/${rootExecutionId}/stream`;
    const ws = new WebSocket(apiClient.getWebSocketUrl(wsUrl));
    wsRef.current = ws;

    // Timeout for detecting completed executions
    // If no events received within 120 seconds of connecting, assume execution is done
    // This needs to be long enough to handle slow LLM responses and tool calls
    let reconnectTimeoutId: NodeJS.Timeout | null = null;
    let hasReceivedEvent = false;

    const clearReconnectTimeout = () => {
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
      }
    };

    ws.onopen = () => {
      console.log('🔌 WebSocket reconnected');
      setIsConnected(true);

      // Set timeout to detect completed executions
      reconnectTimeoutId = setTimeout(() => {
        if (!hasReceivedEvent && wsRef.current === ws) {
          console.log('⏱️ No events received after reconnect (120s) - execution likely completed');
          setIsRunning(false);
          setIsConnected(false);
          ws.close();
          wsRef.current = null;
        }
      }, 120000); // 120 seconds
    };

    // Use the same message handlers as the main WebSocket
    ws.onmessage = (event) => {
      hasReceivedEvent = true;
      clearReconnectTimeout();

      const data = JSON.parse(event.data);
      console.log('🔌 WS Event (reconnected):', data.type, 'agent_execution_id:', data.agent_execution_id, data);

      if (data.type === 'llm_end') {
        addEvent({
          type: 'llm_end',
          execution_id: data.agent_execution_id,
          content: data.content || [],
          input_tokens: data.input_tokens || 0,
          output_tokens: data.output_tokens || 0,
          cost_usd: data.cost_usd || 0,
          duration_ms: data.duration_ms || 0,
          model: data.model || '',
          stop_reason: data.stop_reason || '',
        });

        for (const block of data.content) {
          if (block.type === 'text' && block.text) {
            appendText(block.text + '\n\n');
          } else if (block.type === 'tool_use') {
            addToolCall(block.id, block.name, block.input || {});
          }
        }
      } else if (data.type === 'tool_call_started') {
        addEvent({
          type: 'tool_call_started',
          execution_id: data.agent_execution_id,
          tool_use_id: data.tool_use_id,
          tool_name: data.tool_name,
          tool_input: data.tool_input || {},
          is_async: data.is_async || false,
        });

        const exists = partsRef.current.some(
          p => p.type === 'tool-call' && p.toolCallId === data.tool_use_id
        );
        if (!exists) {
          addToolCall(data.tool_use_id, data.tool_name, data.tool_input || {});
        }

        // Handle ask_user_permission
        if (data.tool_name === 'ask_user_permission') {
          const input = data.tool_input || {};
          if (input.question) {
            setWaitingForUserInput({
              executionId: data.agent_execution_id,
              toolUseId: data.tool_use_id,
              question: input.question,
              options: input.options,
              inputType: input.input_type || 'confirmation',
              timeoutAt: input.timeout_at,
            });
            updateAssistantMessage(true);
            setIsRunning(false);
          }
        }
      } else if (data.type === 'tool_call_completed') {
        addEvent({
          type: 'tool_call_completed',
          execution_id: data.agent_execution_id,
          tool_use_id: data.tool_use_id,
          tool_name: data.tool_name,
          result: data.result,
          is_error: data.is_error || false,
          duration_ms: data.duration_ms || 0,
        });
        updateToolResult(data.tool_use_id, data.result, data.is_error || false);

        // Handle ask_user pending status
        if (data.tool_name === 'ask_user' || data.tool_name === 'ask_user_permission') {
          const result = typeof data.result === 'string' ? JSON.parse(data.result) : data.result;
          if (result && result.status === 'pending' && result.question) {
            setWaitingForUserInput({
              executionId: data.agent_execution_id,
              toolUseId: data.tool_use_id,
              question: result.question,
              options: result.options,
              inputType: result.input_type || 'free_text',
              timeoutAt: result.timeout_at,
              formSchema: result.form_schema,
              formValues: result.form_values,
              formAction: result.form_action,
              resourceType: result.resource_type,
              resourceId: result.resource_id,
            });
            updateAssistantMessage(true);
            setIsRunning(false);
          }
        }
      } else if (data.type === 'subagent_started') {
        addEvent({
          type: 'subagent_started',
          execution_id: data.agent_execution_id,
          parent_execution_id: data.parent_execution_id,
          agent_type: data.agent_type || 'unknown',
          goal: data.goal || '',
        });
      } else if (data.type === 'subagent_completed') {
        addEvent({
          type: 'subagent_completed',
          execution_id: data.agent_execution_id,
          parent_execution_id: data.parent_execution_id,
          duration_ms: data.duration_ms || 0,
          total_input_tokens: data.total_input_tokens || 0,
          total_output_tokens: data.total_output_tokens || 0,
          cost_usd: data.cost_usd || 0,
        });
      } else if (data.type === 'llm_start') {
        // ProCode subagent LLM start (reconnected)
        addEvent({
          type: 'llm_start',
          execution_id: data.agent_execution_id,
          agent_name: data.agent_name || '',
        });
      } else if (data.type === 'llm_end') {
        // ProCode subagent LLM end (reconnected)
        addEvent({
          type: 'llm_end',
          execution_id: data.agent_execution_id,
          agent_name: data.agent_name || '',
          input_tokens: data.input_tokens || 0,
          output_tokens: data.output_tokens || 0,
          cost_usd: data.cost_usd || 0,
        });
      } else if (data.type === 'agent_start') {
        // ProCode subagent agent start (reconnected)
        addEvent({
          type: 'agent_start',
          execution_id: data.agent_execution_id,
          agent_name: data.agent_name || '',
        });
      } else if (data.type === 'tool_progress') {
        // Tool progress update (reconnected)
        console.log('📊 Tool progress (reconnected):', data.tool_call_id, data.progress);
        addEvent({
          type: 'tool_progress',
          execution_id: data.agent_execution_id,
          tool_call_id: data.tool_call_id,
          tool_name: data.tool_name,
          progress: data.progress,
        });
      } else if (data.type === 'execution_completed') {
        addEvent({
          type: 'execution_completed',
          execution_id: data.agent_execution_id,
          duration_ms: data.duration_ms || 0,
          total_input_tokens: data.total_input_tokens || 0,
          total_output_tokens: data.total_output_tokens || 0,
          cost_usd: data.cost_usd || 0,
        });

        const isRootAgent = !data.agent_execution_id || data.agent_execution_id === currentAgentExecutionIdRef.current;
        if (isRootAgent) {
          updateAssistantMessage(true);
          setIsRunning(false);
          currentAssistantIdRef.current = null;
          rootExecutionIdRef.current = null;
          currentAgentExecutionIdRef.current = null;
          ws.close();
        }
      } else if (data.type === 'tools_finder_started') {
        addEvent({
          type: 'tools_finder_started',
          execution_id: data.agent_execution_id,
          unavailable_tools: data.unavailable_tools || [],
          count: data.count || 0,
        });
      } else if (data.type === 'tools_finder_completed') {
        addEvent({
          type: 'tools_finder_completed',
          execution_id: data.agent_execution_id,
          total_tools: data.total_tools || 0,
          alternatives_found: data.alternatives_found || 0,
          results: data.results || {},
        });
      } else if (data.type === 'waiting_for_user_input') {
        setWaitingForUserInput({
          executionId: data.agent_execution_id,
          toolUseId: data.tool_use_id,
          question: data.question,
          options: data.options,
          inputType: data.input_type || 'free_text',
          timeoutAt: data.timeout_at,
          formSchema: data.form_schema,
          formValues: data.form_values,
          formAction: data.form_action,
          resourceType: data.resource_type,
          resourceId: data.resource_id,
        });
        updateAssistantMessage(true);
        setIsRunning(false);
      } else if (data.type === 'execution_failed') {
        const errorMsg = data.error || data.message || data.reason || 'Agent execution failed';
        const errorDetail = data.error_detail ? `\n\n\`\`\`\n${data.error_detail}\n\`\`\`` : '';
        const hasContent = partsRef.current.length > 0 && partsRef.current.some(p => p.type === 'text' && (p as any).text?.trim());
        const prefix = hasContent ? '\n\n' : '';
        appendText(`${prefix}❌ **Error:** ${errorMsg}${errorDetail}`);
        updateAssistantMessage(true);
        setIsRunning(false);
        currentAssistantIdRef.current = null;
        ws.close();
      } else if (data.type === 'error') {
        const hasContent = partsRef.current.length > 0 && partsRef.current.some(p => p.type === 'text' && (p as any).text?.trim());
        const prefix = hasContent ? '\n\n' : '';
        appendText(`${prefix}❌ **Error:** ${data.error || data.message || 'Unknown error'}`);
        updateAssistantMessage(true);
        setIsRunning(false);
        currentAssistantIdRef.current = null;
        ws.close();
      }
    };

    ws.onerror = (error) => {
      // Silently handle reconnect errors - execution may have already completed
      // This is expected behavior when navigating to a finished conversation
      console.log('🔌 WebSocket reconnect failed (execution may have completed):', error);
      clearReconnectTimeout();
      setIsRunning(false);
      setIsConnected(false);
      wsRef.current = null;
    };

    ws.onclose = (event) => {
      console.log('🔌 WebSocket closed (reconnected):', { code: event.code, reason: event.reason });
      clearReconnectTimeout();
      wsRef.current = null;
      setIsConnected(false);
      // On reconnect close, just mark as not running - don't modify message state
      setIsRunning(false);
    };
  }, [messages, addEvent, appendText, addToolCall, updateToolResult, updateAssistantMessage]);

  // Sync events from external source (for refresh)
  const syncEvents = useCallback((newEvents: ExecutionEvent[]) => {
    console.log('🔄 Syncing events externally:', newEvents.length);
    eventsRef.current = newEvents;
    setExecutionEvents(newEvents);
  }, []);

  return {
    runtime,
    executionEvents,
    isRunning,
    isConnected,  // WebSocket connection status
    clearMessages,  // For "New Chat" functionality
    setMessages,    // For restoring messages when switching conversations
    syncEvents,     // For refreshing events from backend
    messages,       // Current messages (for caching)
    addEvent,       // For testing - inject events directly
    injectMockNestedEvents, // For testing - inject full mock scenario
    injectTestMessage, // For testing - inject test assistant message
    waitingForUserInput,    // ask_user: current question waiting for user response
    submitUserResponse,     // ask_user: submit user's response to resume agent
    sendMessage, // For form submissions - send new message to agent (stable reference via ref pattern)
    reconnectToExecution, // Reconnect to an active execution stream
    cancelExecution: handleCancel, // Stop running agent execution
  };
}
